import React, { ChangeEvent, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMinusCircle,
  faPlusCircle,
  faRemove,
  faSearchMinus,
  faSearchPlus
} from '@fortawesome/free-solid-svg-icons';
import {
  InfluencerLink,
  PARTNER_ROLE_NAME,
  PlansENUM,
  SelectOptionProps,
  UserItemProps,
  UserRegion
} from '..';
import { Button, Form } from '../../../../components';
import {
  InstructionWrapper,
  TextField,
  ToggleSwitch,
  Checkbox,
  DatePicker
} from '../../../../components/fields';
import { Regexes } from '../../../../definitions';
import { AlertTypeEnum, ESearchType, StateTopicEnum } from '../../../../enums';
import { publish, useStateReducer } from '../../../../hooks';
import { validateObject } from '../../../../utilities';
import { goFetch } from '../../../../utilities/goFetch';
import Heading from '../../../../_global/components/heading/Heading';
import './_styles.scss';
import {
  Card,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  capitalize
} from '@mui/material';
import { Reorder } from 'framer-motion';
import { roleDataService } from '../../../roles/services';
import { UserPermissions } from '../../../../../src/definitions';
import { securityService } from '../../../../../src/_global/services';
import userDataService from './../../services/dataService';
import {
  IGetRequestLimit,
  IRequestLimitStateObject,
  IResetRequestLimit
} from '../../../../interfaces';
import Select from 'react-select';
import config from '../../../../_config/global';
import { hideAlert, showAlert } from '../../../../components/alert/Alert';
import moment from 'moment';

const partnerCategoryLabels = [
  'Influencer',
  'Blogger',
  'Course Creator',
  'Author'
];

export const partnerCategoryOptions = partnerCategoryLabels.map((label) => ({
  label,
  value: label.toLowerCase()
}));

export default function EditItem({
  data: {
    _id,
    email_address,
    first_name,
    last_name,
    source,
    admin_right,
    email_verified,
    awayz_access,
    user_roles,
    influencer_links = [],
    userRegion,
    partner_category
  }
}: UserItemProps) {
  const [state, setState] = useStateReducer({
    email_address,
    first_name,
    last_name,
    source: source ? source : '',
    admin_right: admin_right ? admin_right : false,
    email_verified: email_verified ? email_verified : false,
    awayz_access: awayz_access
      ? awayz_access
      : {
          active: true,
          awayz_forever_enum: PlansENUM.free
        },
    user_roles: user_roles ? user_roles : [],
    roles: [],
    influencer_links: influencer_links ? influencer_links : [],
    awayz_remaining_calls: null,
    bilt_remaining_calls: null,
    bilt_number_of_calls: null,
    request_limit_awayz_hotel: null,
    request_limit_awayz_flight: null,
    request_limit_bilt: null,
    userRegion: userRegion,
    partner_primary_category: partner_category?.primary ?? '',
    partner_secondary_category: partner_category?.secondary ?? '',
    fromDate: moment(new Date(), config.dateFormat).subtract(30, 'days'),
    toDate: moment(new Date(), config.dateFormat)
  });

  const [userRegions, setUserRegions] = useState<UserRegion[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<
    ChangeEvent<HTMLSelectElement> | string
  >();

  const can_edit_awayz_access = securityService.checkRoleAccess(
    UserPermissions.editUserAwayzAccess
  );
  const [displayName, setDisplayName] = React.useState(
    `${first_name ?? ''} ${last_name ? last_name + ' | ' : ''} ${
      email_address ?? ''
    }
`.trim()
  );
  const [numSearches, setNumSearches] = useState<number>(0);
  const getRequestLimitArgs: IGetRequestLimit = { _id };

  const initData = async () => {
    const rolesData = await roleDataService.getRoles();
    setState({ roles: rolesData, influencer_links });

    await Promise.all([
      userDataService.getAwayzUserRequestLimit(getRequestLimitArgs),
      userDataService.getBiltUserRequestLimit(getRequestLimitArgs)
    ]).then(([awayzRequestLimitResponse, biltRequestLimitResponse]) => {
      if (awayzRequestLimitResponse && awayzRequestLimitResponse.length) {
        const awayzStateEntry: IRequestLimitStateObject = {};
        for (const limit of awayzRequestLimitResponse) {
          awayzStateEntry[limit.type] = {
            remaining_calls: limit.remaining_calls,
            number_of_calls: limit.number_of_calls,
            search_limit: limit.search_limit,
            is_admin: limit.is_admin
          };
        }
        setState({
          awayz_remaining_calls: awayzStateEntry,
          bilt_remaining_calls: biltRequestLimitResponse
            ? biltRequestLimitResponse.remaining_calls
            : 0,
          bilt_number_of_calls: biltRequestLimitResponse
            ? biltRequestLimitResponse.number_of_calls
            : 0
        });
      }
    });
  };

  const isValidInfluencerLinks = () => {
    let tisValid = true;
    state.influencer_links &&
      state.influencer_links.forEach((element: InfluencerLink) => {
        if (element.code === '' || element.url === '') tisValid = false;
      });
    return tisValid;
  };

  async function handleRequestLimitReset() {
    let envResetRequest: string[] = [];

    if (state.request_limit_awayz_hotel) {
      envResetRequest.push('Awayz_Hotel');
    }

    if (state.request_limit_awayz_flight) {
      envResetRequest.push('Awayz_Flight');
    }

    if (state.request_limit_bilt) {
      envResetRequest.push('Bilt');
    }

    const resetRequestLimitArgs: IResetRequestLimit = {
      key: _id,
      env: envResetRequest
    };
    const res = await userDataService.resetUserRequestLimit(
      resetRequestLimitArgs
    );

    await Promise.all([
      userDataService.getAwayzUserRequestLimit(getRequestLimitArgs),
      userDataService.getBiltUserRequestLimit(getRequestLimitArgs)
    ]).then(async ([awayzRequestLimitResponse, biltRequestLimitResponse]) => {
      if (awayzRequestLimitResponse && awayzRequestLimitResponse.length) {
        const awayzStateEntry: IRequestLimitStateObject = {};
        for (const limit of awayzRequestLimitResponse) {
          awayzStateEntry[limit.type] = {
            remaining_calls: limit.remaining_calls,
            number_of_calls: limit.number_of_calls,
            search_limit: limit.search_limit,
            is_admin: limit.is_admin
          };
        }
        setState({
          awayz_remaining_calls: awayzStateEntry,
          bilt_remaining_calls: biltRequestLimitResponse
            ? biltRequestLimitResponse.remaining_calls
            : 0,
          bilt_number_of_calls: biltRequestLimitResponse
            ? biltRequestLimitResponse.number_of_calls
            : 0
        });
      }
    });
  }

  const isValid = () => {
    if (errors['email_address']) return false;
    if (errors['first_name']) return false;
    if (errors['last_name']) return false;
    if (!isValidInfluencerLinks()) return false;
    return true;
  };

  const errors = validateObject(
    {
      first_name: {
        regex: Regexes.anyContentRegex,
        message: 'First and Last Name are required.'
      },
      last_name: {
        regex: Regexes.anyContentRegex,
        message: 'First and Last Name are required.'
      },
      email_address: {
        regex: Regexes.emailRegex,
        message: 'Please enter a valid email address.'
      },
      partner_primary_category: {
        regex: Regexes.anyContentRegex,
        message: 'Please select a primary category.'
      }
    },
    state
  );

  useEffect(() => {
    const fetchUserRegionData = async () => {
      try {
        const fetchedUserRegions = await (
          await fetch(`${config.URL}${config.endpoints.regions.getRegions}`)
        ).json();
        if (fetchedUserRegions) {
          setUserRegions(fetchedUserRegions.userRegions);
        }
      } catch (e) {
        if (e instanceof Error)
          showAlert({
            content: e.message
              ? e.message
              : 'There was an error fetching User Region data, please try again.',
            options: {
              actions: [
                {
                  text: 'Close',
                  onClick: () => {
                    hideAlert();
                  }
                }
              ],
              type: AlertTypeEnum.Error
            }
          });
      }
    };

    if (userRegion) {
      setSelectedRegion(userRegion);
    }

    fetchUserRegionData();
  }, []);

  const handleRegionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRegion(event);
    setState({
      userRegion: event
    });
    publish(StateTopicEnum.EditItem, state);
    publish(StateTopicEnum.IsValid, isValid());
  };

  const handleSimulateSearch = async () => {
    const userRequests = await goFetch(
      `${config.endpoints.users.increaseRequestLimit}`
    )
      .withBody({
        num_searches: numSearches,
        changing_user_id: _id,
        search_type: ESearchType.TEST
      })
      .post()
      .catch((err) => {
        showAlert({
          content: 'Test Searches were not created successfully.',
          options: {
            actions: [{ text: 'Okay', onClick: hideAlert, primary: true }],
            type: AlertTypeEnum.Info
          }
        });
      });
    if (userRequests) {
      showAlert({
        content: 'Test Searches have been created',
        options: {
          actions: [{ text: 'Okay', onClick: hideAlert, primary: true }],
          type: AlertTypeEnum.Info
        }
      });
    }
    setNumSearches(0);
  };

  const handleRemoveSimulatedSearches = async () => {
    const userRequests = await goFetch(
      `${config.endpoints.users.removeTestSearches}`
    )
      .withBody({
        changing_user_id: _id,
        fromDate: moment(state.fromDate).startOf('day'),
        toDate: moment(state.toDate).endOf('day')
      })
      .post();
    if (userRequests) {
      showAlert({
        content: 'All Test Searches have been deleted.',
        options: {
          actions: [{ text: 'Okay', onClick: hideAlert, primary: true }],
          type: AlertTypeEnum.Info
        }
      });
    }
  };

  useEffect(() => {
    setDisplayName(
      `${state.first_name ?? ''} ${
        state.last_name ? state.last_name + ' | ' : ''
      } ${state.email_address ?? ''}
    `.trim()
    );
    publish(StateTopicEnum.IsValid, isValid());
    publish(StateTopicEnum.EditItem, { ...state });
    // eslint-disable-next-line
  }, [
    state.email_address,
    state.first_name,
    state.last_name,
    state.source,
    state.admin_right,
    state.awayz_access,
    state.user_roles,
    state.influencer_links,
    state.password_reset,
    state.email_verified,
    state.userRegion,
    state.partner_primary_category,
    state.partner_secondary_category,
    state.request_limit_bilt,
    state.request_limit_awayz,
    state.bilt_remaining_calls,
    state.awayz_remaining_calls
  ]);

  useEffect(() => {
    initData();
  }, []);

  const handleState = (key: string, value: any) => {
    setState({ ...state, [key]: value });

    publish(StateTopicEnum.EditItem, state);
    publish(StateTopicEnum.IsValid, isValid());
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string
  ) => {
    let value: string | boolean = event.currentTarget.value;

    // @ts-ignore
    if (event.target.type === 'checkbox') value = event.target.checked;
    setState({ [key]: value });
  };

  const handleRole = (checked: boolean, value: string) => {
    let selectedArray: string[] = [...state.user_roles];

    if (checked) {
      if (!selectedArray.includes(value)) selectedArray.push(value);
    } else {
      selectedArray = selectedArray.filter((item) => item !== value);
    }
    setState({ user_roles: selectedArray });
  };

  const handleAwayzAccess = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value: string | boolean = (event.target as HTMLInputElement).value;
    let mapAccess: any = { ...state.awayz_access };

    switch (value) {
      case PlansENUM.free:
        mapAccess.active = true;
        mapAccess.awayz_forever_enum = PlansENUM.free;
        break;
      case PlansENUM.gold:
        mapAccess.active = true;
        mapAccess.awayz_forever_enum = PlansENUM.gold;
        break;
      case PlansENUM.platinum:
        mapAccess.active = true;
        mapAccess.awayz_forever_enum = PlansENUM.platinum;
        break;
    }
    setState({ awayz_access: mapAccess });
  };

  const getAwayzAccessValue = () => {
    return awayz_access && awayz_access.awayz_forever_enum
      ? awayz_access.awayz_forever_enum
      : PlansENUM.free;
  };

  const primary_partner_category = partnerCategoryOptions.find(
    (i: any) => i.value === partner_category?.primary
  );
  const secondary_partner_category = partnerCategoryOptions.find(
    (i: any) => i.value === partner_category?.secondary
  );

  return (
    <div id='user-edit-item'>
      <Heading name={displayName} />
      <Form title='Personal Details'>
        <InstructionWrapper
          text='Enter the accounts first and last names here.'
          error={errors['first_name'] || errors['last_name']}
          row
        >
          <TextField
            label='First Name'
            value={state.first_name}
            onChange={(event) => handleChange(event, 'first_name')}
            error={!!errors['first_name']}
          />
          <TextField
            label='Last Name'
            key='last_name'
            value={state.last_name}
            onChange={(event) => handleChange(event, 'last_name')}
            error={!!errors['last_name']}
          />
        </InstructionWrapper>
        <InstructionWrapper
          text='Enter the accounts email address'
          error={errors['email_address']}
          row
        >
          <TextField
            label='Email Address'
            value={state.email_address}
            error={!!errors['email_address']}
            onChange={(event) => handleChange(event, 'email_address')}
          />
          {securityService.checkRoleAccess(UserPermissions.viewUserSource) ? (
            <TextField
              label='Source'
              value={state.source}
              onChange={(event) => handleChange(event, 'source')}
              readOnly={
                !securityService.checkRoleAccess(UserPermissions.editUserSource)
              }
            />
          ) : (
            <p></p>
          )}
        </InstructionWrapper>

        <InstructionWrapper
          text='Toggle the switch to enable the ability to reset password'
          row
        >
          <InstructionWrapper>
            <FormLabel>
              Password Reset
              <InstructionWrapper row>
                <ToggleSwitch
                  label={state.is_password_reset ? 'Yes' : 'No'}
                  className={'display-block'}
                  value={state.is_password_reset}
                  onChange={(event) => handleChange(event, 'is_password_reset')}
                />
              </InstructionWrapper>
            </FormLabel>
            {state.is_password_reset ? (
              <TextField
                error={!state.password_reset}
                onChange={(event) => handleChange(event, 'password_reset')}
                label='Password'
                value={state.password_reset}
              ></TextField>
            ) : (
              <p></p>
            )}
          </InstructionWrapper>
          <FormLabel>
            Email Verified
            <InstructionWrapper row>
              <ToggleSwitch
                label={state.email_verified ? 'Yes' : 'No'}
                className={'display-block'}
                value={state.email_verified}
                onChange={(event) => handleChange(event, 'email_verified')}
              />
            </InstructionWrapper>
          </FormLabel>
        </InstructionWrapper>
      </Form>

      {securityService.checkRoleAccess(UserPermissions.viewUserAwayzAccess) ? (
        <Form title='Environment Access (Awayz.com)'>
          <InstructionWrapper
            row
            text={"'Stripe Access' is linked to payments"}
          >
            <FormLabel>
              Stripe Access
              <div style={{ margin: '14px 0px' }}>
                {capitalize(
                  awayz_access?.awayz_enum
                    ? awayz_access?.awayz_enum
                    : 'Unknown'
                )}
              </div>
            </FormLabel>
            <InstructionWrapper row>
              <FormLabel>
                Admin Portal
                <InstructionWrapper row>
                  <ToggleSwitch
                    label={state.admin_right ? 'Yes' : 'No'}
                    className={'display-block'}
                    name={'awayz_access'}
                    value={state.admin_right}
                    onChange={(event) => handleChange(event, 'admin_right')}
                  />
                  <div></div>
                </InstructionWrapper>
              </FormLabel>
            </InstructionWrapper>
          </InstructionWrapper>

          <InstructionWrapper
            text={
              "'Manual Access' to override 'Stripe Access' \n Reason: demote or promote user "
            }
            row
          >
            <FormLabel>
              Manual Access
              <RadioGroup
                row
                aria-labelledby='awayz-environment-access-label'
                defaultValue={getAwayzAccessValue}
                name='awayz-access-radio-buttons-group'
                onChange={handleAwayzAccess}
              >
                <FormControlLabel
                  value={PlansENUM.free}
                  control={<Radio />}
                  label='None'
                  disabled={!can_edit_awayz_access}
                />
                <FormControlLabel
                  value={PlansENUM.gold}
                  control={<Radio />}
                  label='Awayz Gold'
                  disabled={!can_edit_awayz_access}
                />
                <FormControlLabel
                  value={PlansENUM.platinum}
                  control={<Radio />}
                  label='Awayz Platinum'
                  disabled={!can_edit_awayz_access}
                />
              </RadioGroup>
            </FormLabel>
          </InstructionWrapper>
        </Form>
      ) : null}

      <Form title='User Region'>
        <InstructionWrapper text={'Select the user region'} row>
          <FormControl>
            <Select
              id='user_region'
              value={
                selectedRegion
                  ? { label: selectedRegion }
                  : { label: userRegion }
              }
              placeholder='Please select a region'
              onChange={(selectedOption: any) =>
                handleRegionChange(selectedOption.value)
              }
              options={
                userRegions && userRegions.length > 0
                  ? userRegions.map((userRegion: any) => ({
                      label: userRegion.name,
                      value: userRegion.code
                    }))
                  : [{ label: 'EMPTY', value: 'EMPTY' }]
              }
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  padding: '0.5rem',
                  fontSize: '1rem',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  color: 'white !important',
                  backgroundColor: '#0000001A',
                  margin: '12px 0px'
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected
                    ? '#0000003A'
                    : 'transparent',
                  color: state.isSelected ? 'white' : 'black',
                  ':hover': {
                    backgroundColor: '#0000003A',
                    color: 'white'
                  }
                }),
                singleValue: (provided, state) => ({
                  ...provided,
                  color: 'white'
                })
              }}
            />
          </FormControl>
        </InstructionWrapper>
      </Form>

      <Heading name='Request Limit' />
      <Form title='Remaining calls/limit'>
        <InstructionWrapper>
          {state.awayz_remaining_calls ? (
            <Form title='Awayz.com'>
              <div
                style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}
              >
                <FormControlLabel
                  style={{ margin: '0rem', flex: 1 }}
                  control={
                    state.awayz_remaining_calls &&
                    state.awayz_remaining_calls.Hotel.remaining_calls !==
                      'Infinity' &&
                    state.awayz_remaining_calls.Hotel.number_of_calls !== 0 ? (
                      <Checkbox label='Hotel' />
                    ) : (
                      <Checkbox label='Hotel' disabled />
                    )
                  }
                  label={
                    state.awayz_remaining_calls &&
                    state.awayz_remaining_calls.Hotel.remaining_calls !==
                      'Infinity'
                      ? `${
                          state.awayz_remaining_calls.Hotel.remaining_calls
                        } / ${
                          state.awayz_remaining_calls.Hotel.number_of_calls +
                          state.awayz_remaining_calls.Hotel.remaining_calls
                        }`
                      : state.awayz_remaining_calls.Hotel.remaining_calls
                  }
                  onChange={(event) => {
                    setState({
                      request_limit_awayz_hotel: (
                        event.target as HTMLInputElement
                      ).checked
                    });
                  }}
                />
                <FormControlLabel
                  style={{ margin: '0rem', flex: 1 }}
                  control={
                    state.awayz_remaining_calls &&
                    state.awayz_remaining_calls.Flight.remaining_calls !==
                      'Infinity' &&
                    state.awayz_remaining_calls.Flight.number_of_calls !== 0 ? (
                      <Checkbox label='Flights' />
                    ) : (
                      <Checkbox label='Flights' disabled />
                    )
                  }
                  label={
                    state.awayz_remaining_calls &&
                    state.awayz_remaining_calls.Flight.remaining_calls !==
                      'Infinity'
                      ? `${
                          state.awayz_remaining_calls.Flight.remaining_calls
                        } / ${
                          state.awayz_remaining_calls.Flight.number_of_calls +
                          state.awayz_remaining_calls.Flight.remaining_calls
                        }`
                      : state.awayz_remaining_calls.Flight.remaining_calls
                  }
                  onChange={(event) => {
                    setState({
                      request_limit_awayz_flight: (
                        event.target as HTMLInputElement
                      ).checked
                    });
                  }}
                />
              </div>
            </Form>
          ) : null}

          {state.bilt_remaining_calls ? (
            <Form title='Bilt'>
              <FormControlLabel
                style={{ margin: '0rem' }}
                control={
                  state.bilt_remaining_calls &&
                  state.bilt_remaining_calls !== 'Infinity' &&
                  state.bilt_number_of_calls !== 0 ? (
                    <Checkbox label='Bilt.awayz.com' />
                  ) : (
                    <Checkbox label='Bilt.awayz.com' disabled />
                  )
                }
                label={
                  state.bilt_remaining_calls &&
                  state.bilt_remaining_calls !== 'Infinity'
                    ? `${state.bilt_remaining_calls} / ${
                        state.bilt_number_of_calls + state.bilt_remaining_calls
                      }`
                    : state.bilt_remaining_calls
                }
                onChange={(event) => {
                  setState({
                    request_limit_bilt: (event.target as HTMLInputElement)
                      .checked
                  });
                }}
              />
            </Form>
          ) : null}

          {state.awayz_remaining_calls || state.bilt_remaining_calls ? (
            <Button
              className='toolbar-sizing'
              text='Reset'
              icon={faMinusCircle}
              primary
              raised
              disabled={
                (state.bilt_number_of_calls === 0 ||
                  state.bilt_remaining_calls === 'Infinity') &&
                (state.awayz_remaining_calls.Hotel.number_of_calls === 0 ||
                  state.awayz_remaining_calls.Hotel.remaining_calls ===
                    'Infinity') &&
                (state.awayz_remaining_calls.Flight.number_of_calls === 0 ||
                  state.awayz_remaining_calls.Flight.remaining_calls ===
                    'Infinity')
              }
              onClick={handleRequestLimitReset}
            />
          ) : null}
        </InstructionWrapper>
      </Form>

      {securityService.checkRoleAccess(UserPermissions.isSuperUser) ? (
        <Form title='Simulate Searches'>
          <InstructionWrapper row>
            <FormLabel>Add Test Searches</FormLabel>
            <FormControl>
              <TextField
                label='Number of Searches.'
                variant='outlined'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setNumSearches(parseInt(e.target.value));
                }}
              ></TextField>
            </FormControl>

            <Button
              className='toolbar-sizing'
              icon={faPlusCircle}
              primary
              raised
              onClick={handleSimulateSearch}
            />
          </InstructionWrapper>
          <InstructionWrapper row>
            <FormLabel>Remove all test Searches</FormLabel>
            <div>
              <DatePicker
                label='From'
                className='date-selector-partner'
                value={state.fromDate}
                onChange={(date: Date | null | undefined) => {
                  setState({ fromDate: date });
                }}
              />
              <DatePicker
                label='To'
                className='date-selector-partner'
                minDate={state.fromDate}
                value={state.toDate}
                onChange={(date: Date | null | undefined) => {
                  setState({ toDate: date });
                }}
              />
            </div>
            <Button
              className='toolbar-sizing'
              icon={faMinusCircle}
              primary
              raised
              onClick={handleRemoveSimulatedSearches}
            />
          </InstructionWrapper>
        </Form>
      ) : null}

      <Form>
        <Heading
          style={{ margin: '12px 0px' }}
          name='Influencer Links'
          onClickRight={() => {
            if (!state.influencer_links) {
              state.influencer_links = [];
            }

            let k: InfluencerLink = state.influencer_links.at(-1);

            if (!k || (k.code && k.url)) {
              let j: InfluencerLink = {
                code: '',
                url: ''
              };
              state.influencer_links.push(j);
              handleState('influencer_links', state.influencer_links);
            }
          }}
        />

        <Reorder.Group
          as='span'
          axis='y'
          className='program-card-container'
          values={state.influencer_links}
          onReorder={(newOrder: any[]) => {
            let aux = { ...state.influencer_links };
            aux = [state.influencer_links[0], ...newOrder];
            handleState('influencer_links', aux);
          }}
        >
          {state.influencer_links.map(
            (influencer_link: InfluencerLink, index: number) => {
              let child;
              child = (
                <Card
                  key={'influencer_link_' + index}
                  className='influencer-link-card-edit page-padding'
                >
                  <FontAwesomeIcon
                    className={'remove-program'}
                    icon={faRemove}
                    onClick={() => {
                      if (index > -1) {
                        state.influencer_links.splice(index, 1);
                      }
                      handleState('influencer_links', state.influencer_links);
                    }}
                  />
                  <TextField
                    value={index + 1}
                    style={{ width: '40px' }}
                    readOnly
                    error={errors['InfluencerLinks'] ? true : false}
                  ></TextField>
                  <TextField
                    className='next-each-other'
                    label={'Code'}
                    value={influencer_link.code}
                    error={influencer_link.code === ''}
                    onChange={(event) => {
                      state.influencer_links[index].code =
                        event.currentTarget.value;
                      handleState('influencer_links', state.influencer_links);
                    }}
                  ></TextField>

                  <TextField
                    className='next-each-other'
                    label={'Url'}
                    value={influencer_link.url}
                    error={influencer_link.url === ''}
                    onChange={(event) => {
                      state.influencer_links[index].url =
                        event.currentTarget.value;
                      handleState('influencer_links', state.influencer_links);
                    }}
                  ></TextField>
                </Card>
              );

              return (
                <Reorder.Item
                  key={`reorder_${index}`}
                  value={influencer_link}
                  drag={false}
                  draggable={false}
                >
                  {child}
                </Reorder.Item>
              );
            }
          )}
        </Reorder.Group>
      </Form>

      <Form>
        <Heading name='Account Roles'></Heading>

        {state.roles?.map((role: any, index: number) => {
          return role.is_active || state.user_roles.includes(role._id) ? (
            <div key={'user_roles_card_' + index}>
              <InstructionWrapper row text={role.rolename + ' Role'}>
                <Card className='program-card-edit page-padding'>
                  {
                    <div key={role.rolename + index} className='flex-row'>
                      <div className='label-col'>
                        <TextField value={role.rolename} readOnly></TextField>
                      </div>

                      <div className='switch-col'>
                        <ToggleSwitch
                          label=''
                          name={role._id}
                          value={state.user_roles.includes(role._id)}
                          onChange={(event) =>
                            handleRole(event.target.checked, role._id)
                          }
                          readOnly={!role.is_active}
                        />
                      </div>
                    </div>
                  }
                </Card>
              </InstructionWrapper>
            </div>
          ) : null;
        })}
      </Form>
      {state.user_roles.includes(
        state.roles?.find((i: any) => i.rolename === PARTNER_ROLE_NAME)?._id
      ) && (
        <Form title='Partner Label'>
          <InstructionWrapper
            text={'Partner Label Required'}
            row
            error={errors['partner_primary_category']}
          >
            <FormControl>
              <Select
                id='partner_primary_category'
                defaultValue={primary_partner_category}
                placeholder='Primary Partner Label'
                onChange={(selectedOption: any) =>
                  setState({
                    partner_primary_category: selectedOption.value
                  })
                }
                aria-errormessage={
                  state.partner_primary_category
                    ? 'please select a primary label'
                    : ''
                }
                options={partnerCategoryOptions}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    padding: '0.5rem',
                    fontSize: '1rem',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    color: 'white !important',
                    backgroundColor: '#0000001A',
                    margin: '12px 0px'
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? '#0000003A'
                      : 'transparent',
                    color: state.isSelected ? 'white' : 'black',
                    ':hover': {
                      backgroundColor: '#0000003A',
                      color: 'white'
                    }
                  }),
                  singleValue: (provided, state) => ({
                    ...provided,
                    color: 'white'
                  })
                }}
              />
              <Select
                id='partner_secondary_category'
                defaultValue={secondary_partner_category}
                placeholder='Secondary Partner Label'
                onChange={(selectedOption: any) =>
                  setState({
                    partner_secondary_category: selectedOption.value
                  })
                }
                options={partnerCategoryOptions}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    padding: '0.5rem',
                    fontSize: '1rem',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    color: 'white !important',
                    backgroundColor: '#0000001A',
                    margin: '12px 0px'
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? '#0000003A'
                      : 'transparent',
                    color: state.isSelected ? 'white' : 'black',
                    ':hover': {
                      backgroundColor: '#0000003A',
                      color: 'white'
                    }
                  }),
                  singleValue: (provided, state) => ({
                    ...provided,
                    color: 'white'
                  })
                }}
              />
            </FormControl>
          </InstructionWrapper>
        </Form>
      )}
    </div>
  );
}
